import { useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TemplateSender, WindowNotice } from '@/components/WindowBar';
import { useFallbackName, useWindowState } from '@/lib/window';
import { toast } from 'sonner';
import { ArrowLeft, Check, CheckCheck, Clock, AlertCircle, Send, Paperclip, X, Reply, SmilePlus } from 'lucide-react';
import { apiFetch, makeClient } from '@/lib/api';
import { formatChatIdentity } from '@/lib/phone';
import { useT } from '@/lib/i18n';
import { useFreeQuotaToast } from '@/lib/free-quota';

interface Media { url?: string; mime_type?: string; filename?: string }
interface Msg {
  id: string;
  wa_message_id?: string;
  direction: 'in' | 'out';
  type: string;
  status: string;
  payload?: {
    body?: string; caption?: string; media?: Media; name?: string; options?: string[];
    latitude?: number; longitude?: number;
    poll?: { name?: string; options?: string[] };
    poll_vote?: { selected?: string[]; poll_message_id?: string };
    referral?: { source_type?: string; source_url?: string; headline?: string; body?: string };
    template?: string; variables?: string[]; delivered_via?: string;
  };
  error?: string;
  created_at: string;
}

const MEDIA_LABEL: Record<string, string> = {
  image: '📷 Imagem', video: '🎬 Vídeo', audio: '🎵 Áudio', document: '📄 Documento',
  sticker: '🌟 Figurinha', location: '📍 Localização', contact: '👤 Contato', poll: '📊 Enquete',
};

function caption(m: Msg): string {
  return m.payload?.caption || m.payload?.body || '';
}

// MediaBubble renderiza a mídia quando há URL (entrada sempre; saída por URL).
function MediaBubble({ m }: { m: Msg }) {
  const url = m.payload?.media?.url;
  const cap = caption(m);
  if (url) {
    if (m.type === 'image' || m.type === 'sticker')
      return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="max-h-64 rounded-md" /></a>;
    if (m.type === 'video') return <video src={url} controls className="max-h-64 rounded-md" />;
    if (m.type === 'audio') return <audio src={url} controls className="w-56 max-w-full" />;
    if (m.type === 'document')
      return <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline">📄 {m.payload?.media?.filename || cap || 'Documento'}</a>;
  }
  // Sem URL (ex.: saída por base64) ou tipo sem mídia: rótulo + legenda.
  const label = m.payload?.body || cap || MEDIA_LABEL[m.type] || m.type;
  return <span className="whitespace-pre-wrap break-words">{label}</span>;
}

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'document', 'sticker']);

// AdOrigin: a conversa começou por um anúncio ou post de "clique para o
// WhatsApp" (só no oficial). Mostrar de qual anúncio o contato veio é o que a
// Meta não mostra em lugar nenhum da conversa.
function AdOrigin({ r }: { r: NonNullable<NonNullable<Msg['payload']>['referral']> }) {
  const t = useT();
  const title = r.headline || r.body || r.source_url;
  return (
    <div className="mb-1 flex flex-col rounded border-l-4 border-[#25d366] bg-black/5 px-2 py-1 text-[12px] dark:bg-white/5">
      <span className="font-medium text-[#008069] dark:text-[#25d366]">{t(r.source_type === 'post' ? 'inbox.fromPost' : 'inbox.fromAd')}</span>
      {title && (r.source_url
        ? <a href={r.source_url} target="_blank" rel="noreferrer" className="truncate underline">{title}</a>
        : <span className="truncate">{title}</span>)}
    </div>
  );
}

// ContentView renderiza o conteúdo da bolha conforme o tipo da mensagem.
function ContentView({ m }: { m: Msg }) {
  const p = m.payload || {};
  if (MEDIA_TYPES.has(m.type)) {
    return (
      <div className="flex flex-col gap-1">
        <MediaBubble m={m} />
        {caption(m) && p.media?.url && <span className="whitespace-pre-wrap break-words">{caption(m)}</span>}
      </div>
    );
  }
  if (m.type === 'poll') {
    const name = p.poll?.name || p.name || p.body;
    const opts = p.poll?.options || p.options || [];
    return (
      <div className="flex flex-col gap-1.5">
        <span className="font-medium">📊 {name}</span>
        {opts.map((o, i) => (
          <span key={i} className="rounded border border-black/10 px-2 py-1 text-[13px] dark:border-white/15">○ {o}</span>
        ))}
      </div>
    );
  }
  if (m.type === 'poll_vote') {
    const sel = p.poll_vote?.selected || [];
    return <span>🗳️ <span className="font-medium">{sel.length ? sel.join(', ') : (p.body || 'respondeu à enquete')}</span></span>;
  }
  if (m.type === 'location') {
    const hasCoord = typeof p.latitude === 'number' && typeof p.longitude === 'number';
    const label = `📍 ${p.body || p.name || 'Localização'}`;
    return hasCoord
      ? <a href={`https://maps.google.com/?q=${p.latitude},${p.longitude}`} target="_blank" rel="noreferrer" className="underline">{label}</a>
      : <span>{label}</span>;
  }
  if (m.type === 'contact') return <span>👤 {p.body || p.name || 'Contato'}</span>;
  if (m.type === 'template') {
    // O nome do template e os valores que foram — "template" sozinho não dizia
    // qual mensagem o contato recebeu.
    return (
      <span className="flex flex-col">
        <span className="font-medium">📄 {p.template || 'template'}</span>
        {!!p.variables?.length && <span className="text-[12px] opacity-70">{p.variables.join(' · ')}</span>}
      </span>
    );
  }
  if (m.type === 'reaction') return <span className="text-xl">{p.body || '👍'}</span>;
  if (m.type === 'otp') {
    // Segurança: o código OTP NUNCA é exibido — só o texto de contexto + indicador.
    return (
      <div className="flex flex-col gap-1">
        {p.body && <span className="whitespace-pre-wrap break-words">{p.body}</span>}
        <span className="inline-flex items-center gap-1 text-[13px] opacity-70">🔒 Código enviado (oculto)</span>
      </div>
    );
  }
  return <span className="whitespace-pre-wrap break-words">{p.body || p.caption || MEDIA_LABEL[m.type] || m.type}</span>;
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function StatusTick({ status }: { status: string }) {
  if (status === 'read') return <CheckCheck className="size-3.5 text-sky-500" />;
  if (status === 'delivered') return <CheckCheck className="size-3.5 text-black/45 dark:text-white/45" />;
  if (status === 'sent') return <Check className="size-3.5 text-black/45 dark:text-white/45" />;
  if (status === 'failed' || status === 'error') return <AlertCircle className="size-3.5 text-red-500" />;
  return <Clock className="size-3 text-black/40 dark:text-white/40" />;
}

/** Teto do anexo: base64 infla ~33% e o envio é síncrono (JSON, não multipart). */
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

/** Mensagem que ainda não voltou do servidor (bolha otimista). */
interface Pending {
  localId: string;
  label: string;
  /** message_id devolvido pelo 202: quando ele aparece no histórico, a bolha some. */
  messageId?: string;
  failed?: boolean;
}

/** bytes → base64 sem estourar a pilha em arquivo grande (btoa é por pedaço). */
function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

export function ConversationDrawer({
  open, onOpenChange, chatJid, name, avatarUrl, canSend = false, instanceId, official = false, lastInboundAt, inline = false,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  chatJid: string | null;
  name?: string;
  avatarUrl?: string;
  /** Liga o compositor (o drawer nasceu só-leitura e segue assim onde não é pedido). */
  canSend?: boolean;
  /**
   * Número que envia. Omitido de propósito quando a tela está em "todos os
   * números": aí a afinidade de conversa da API (sticky) reusa o número que JÁ
   * fala com este contato — que é exatamente o que a gente quer, e o que evita
   * aparecer para o cliente com outro número no meio do assunto.
   */
  instanceId?: string;
  /** Projeto no WhatsApp oficial: mostra a janela de 24 horas e o envio de template. */
  official?: boolean;
  /** Última mensagem do contato (vem da lista de conversas). */
  lastInboundAt?: string;
  /** Embutida na página (Playground) em vez de painel lateral. */
  inline?: boolean;
}) {
  const t = useT();
  const qc = useQueryClient();
  const endRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<Pending[]>([]);
  // Resposta citando: a mensagem escolhida vai como quoted_message_id no envio.
  const [replyTo, setReplyTo] = useState<{ wa: string; label: string } | null>(null);
  const [reactingTo, setReactingTo] = useState<string | null>(null);
  const lastTypingAt = useRef(0);
  const lastReadWA = useRef('');

  const isGroup = chatJid?.endsWith('@g.us');
  const ident = formatChatIdentity(chatJid);
  const title = name || ident.text;

  // Histórico UNIFICADO do cliente (sem instance_id → todos os números).
  const { data: msgs, isLoading } = useQuery({
    queryKey: ['history', chatJid],
    enabled: open && !!chatJid,
    refetchInterval: open ? 5000 : false,
    queryFn: async (): Promise<Msg[]> => {
      const r = await makeClient().GET('/conversations/{jid}/messages', {
        params: { path: { jid: chatJid! }, query: {} as { instance_id: string } },
      });
      const list = ((r.data as { data?: Msg[] })?.data ?? []).slice();
      return list.sort((a, b) => a.created_at.localeCompare(b.created_at));
    },
  });

  // Janela de 24h: a última mensagem do contato, pela lista ou pelo histórico
  // aberto (o que for mais recente — o histórico atualiza a cada 5s).
  const lastIn = useMemo(() => {
    const fromHistory = (msgs ?? []).filter((m) => m.direction === 'in').map((m) => m.created_at).sort().pop();
    return [lastInboundAt, fromHistory].filter(Boolean).sort().pop();
  }, [msgs, lastInboundAt]);
  const win = useWindowState(lastIn);
  const fallbackName = useFallbackName(official && canSend);
  const [templateMode, setTemplateMode] = useState(false);
  // Sem janela e sem alternativa, texto não chega: o compositor vira escolha de template.
  const mustTemplate = official && !win.open && !fallbackName;

  useEffect(() => {
    if (msgs && endRef.current) endRef.current.scrollIntoView({ behavior: 'auto' });
  }, [msgs, open, pending]);

  // Trocar de conversa (ou fechar) limpa rascunho e bolhas otimistas: nada de
  // escrever para uma pessoa e o texto reaparecer na conversa de outra.
  useEffect(() => { setDraft(''); setFile(null); setPending([]); setTemplateMode(false); setReplyTo(null); setReactingTo(null); lastReadWA.current = ''; }, [chatJid]);

  // Oficial: conversa aberta = lida. Marca a última mensagem recebida (a Meta
  // marca as anteriores junto) e o contato vê os ✓✓ azuis — o que o WhatsApp do
  // celular faz quando a pessoa abre a conversa.
  useEffect(() => {
    if (!official || !open || !chatJid) return;
    const lastIn = (msgs ?? []).filter((m) => m.direction === 'in' && m.wa_message_id).pop();
    if (!lastIn?.wa_message_id || lastIn.status === 'read' || lastReadWA.current === lastIn.wa_message_id) return;
    lastReadWA.current = lastIn.wa_message_id;
    void apiFetch('POST', `/messages/${encodeURIComponent(lastIn.wa_message_id)}/read`,
      { chat: chatJid, ...(instanceId ? { instance_id: instanceId } : {}) });
  }, [msgs, official, open, chatJid, instanceId]);

  // Oficial: "digitando…" para o contato enquanto a pessoa escreve (no máximo a
  // cada 20 s — a Meta mantém o indicador por até 25 s).
  const onTyping = (v: string) => {
    setDraft(v);
    if (!official || !chatJid || !v.trim()) return;
    const now = Date.now();
    if (now - lastTypingAt.current < 20_000) return;
    lastTypingAt.current = now;
    void apiFetch('POST', '/presence/chat', { to: chatJid, state: 'typing', ...(instanceId ? { instance_id: instanceId } : {}) });
  };

  /** Reage a uma mensagem recebida (a Meta só aceita reação em mensagem do contato). */
  const react = async (m: Msg, emoji: string) => {
    setReactingTo(null);
    if (!chatJid || !m.wa_message_id) return;
    const r = await apiFetch('POST', '/messages/reaction', {
      to: chatJid, quoted_message_id: m.wa_message_id, emoji, ...(instanceId ? { instance_id: instanceId } : {}),
    });
    if (!r.ok) toast.error(sendError((r.data as { code?: string })?.code, (r.data as { message?: string })?.message));
    else void qc.invalidateQueries({ queryKey: ['history', chatJid] });
  };

  // Bolha otimista vive até o histórico trazer a mensagem de verdade (mesmo id)
  // — ou para sempre, se falhou, para o usuário ver que aquilo NÃO foi.
  const ghosts = pending.filter((p) => p.failed || !p.messageId || !(msgs ?? []).some((m) => m.id === p.messageId));

  // 402 da franquia do Free: mensagem do servidor + botão de upgrade.
  const onFreeQuota = useFreeQuotaToast();

  /** Mensagem de erro do envio: código neutro primeiro, texto do servidor depois. */
  const sendError = (code?: string, message?: string): string => {
    if (code) {
      const key = `inbox.sendError.${code}`;
      const translated = t(key);
      if (!translated.endsWith(key)) return translated;
    }
    return message || t('inbox.sendFailed');
  };

  /**
   * send despacha UMA mensagem na conversa aberta. Isto é atendimento 1:1 — não
   * existe envio em massa aqui de propósito: disparo em lote é campanha (com
   * aquecimento e intervalo), e é o caminho que não queima o número.
   */
  const send = async () => {
    if (!chatJid || sending) return;
    const body = draft.trim();
    const attachment = file;
    if (!body && !attachment) return;

    const localId = 'local-' + Date.now();
    const label = attachment ? `📎 ${attachment.name}${body ? ' — ' + body : ''}` : body;
    setPending((prev) => [...prev, { localId, label }]);
    setSending(true);
    setDraft('');
    setFile(null);
    setReplyTo(null);

    // Falhou: a bolha fica marcada como NÃO enviada e o texto/anexo volta para a
    // caixa — quem escreveu não perde o que digitou e reenvia num clique.
    const fail = (code?: string, message?: string, handled = false) => {
      setPending((prev) => prev.map((p) => (p.localId === localId ? { ...p, failed: true } : p)));
      setDraft((cur) => cur || body);
      setFile((cur) => cur ?? attachment);
      if (!handled) toast.error(sendError(code, message));
    };

    try {
      // Só `to` é obrigatório: `instance_id` vai apenas quando a tela fixou um número.
      const base: Record<string, unknown> = { to: chatJid };
      if (instanceId) base.instance_id = instanceId;
      if (replyTo) base.quoted_message_id = replyTo.wa;

      let r;
      if (attachment) {
        // Mídia vai em base64 no próprio JSON (a API aceita url OU base64): o
        // painel não precisa de bucket público para responder um cliente.
        const media = {
          base64: toBase64(await attachment.arrayBuffer()),
          filename: attachment.name,
          mimetype: attachment.type || 'application/octet-stream',
          ...(body ? { caption: body } : {}),
        };
        const path = attachment.type.startsWith('image/') ? '/messages/image' : '/messages/document';
        r = await apiFetch('POST', path, { ...base, media });
      } else {
        r = await apiFetch('POST', '/messages/text', { ...base, body });
      }
      if (!r.ok) {
        const e = r.data as { code?: string; message?: string } | null;
        // Franquia do Free esgotada: quem avisa é o onFreeQuota (texto do servidor +
        // upgrade); aqui só devolvemos o rascunho para a caixa.
        fail(e?.code, e?.message, onFreeQuota(r));
        return;
      }
      const sent = r.data as { message_id?: string; delivery?: { via?: string } } | null;
      const id = sent?.message_id;
      // O número fixado caiu: a API mandou por outro conectado e contou em `delivery`.
      if (sent?.delivery?.via === 'number_fallback') toast.info(t('inbox.sentFromOtherNumber'));
      setPending((prev) => prev.map((p) => (p.localId === localId ? { ...p, messageId: id } : p)));
      void qc.invalidateQueries({ queryKey: ['history', chatJid] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    } catch {
      fail();
    } finally {
      setSending(false);
    }
  };

  const pickFile = (f?: File | null) => {
    if (!f) return;
    if (f.size > MAX_ATTACHMENT_BYTES) { toast.error(t('inbox.attachTooLarge')); return; }
    setFile(f);
  };

  // Ações na mensagem (aparecem ao passar o mouse, coladas ao balão): citar e,
  // em mensagem do contato, reagir.
  const msgActions = (m: Msg, out: boolean) => (
    <div className="invisible flex shrink-0 items-center gap-0.5 rounded-full bg-white/90 p-0.5 shadow group-hover:visible dark:bg-[#202c33]">
      <button title={t('inbox.reply')} onClick={() => setReplyTo({ wa: m.wa_message_id!, label: caption(m) || MEDIA_LABEL[m.type] || m.type })}
        className="rounded-full p-1 hover:bg-black/10 dark:hover:bg-white/10"><Reply className="size-3.5" /></button>
      {!out && (
        <button title={t('inbox.react')} onClick={() => setReactingTo(reactingTo === m.id ? null : m.id)}
          className="rounded-full p-1 hover:bg-black/10 dark:hover:bg-white/10"><SmilePlus className="size-3.5" /></button>
      )}
    </div>
  );

  // O título do diálogo exige o contexto do Dialog; embutida, é um parágrafo.
  const TitleEl = inline ? 'p' : DialogPrimitive.Title;
  const inner = (
    <>
          {/* Header estilo WhatsApp */}
      <div className="flex items-center gap-3 bg-[#008069] px-3 py-2.5 text-white dark:bg-[#202c33]">
        {!inline && (
          <button onClick={() => onOpenChange(false)} className="rounded-full p-1 hover:bg-white/15" aria-label="voltar">
            <ArrowLeft className="size-5" />
          </button>
        )}
        <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/25 text-sm font-semibold">
          {avatarUrl
            ? <img src={avatarUrl} alt="" className="size-full object-cover" />
            : (title || '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <TitleEl className="truncate text-[15px] font-medium leading-tight">{title}</TitleEl>
          <p className="truncate text-xs text-white/80">{isGroup ? t('inbox.group') : ident.text}</p>
        </div>
      </div>

      {/* Mensagens */}
      <div className="flex-1 overflow-y-auto px-4 py-3" style={{ backgroundImage: 'radial-gradient(rgba(0,0,0,0.025) 1px, transparent 0)', backgroundSize: '18px 18px' }}>
        {isLoading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : (msgs ?? []).length === 0 && ghosts.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{t('inbox.noMessages')}</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {(msgs ?? []).map((m) => {
              const out = m.direction === 'out';
              const actions = official && canSend && !!m.wa_message_id && m.type !== 'reaction';
              return (
                <div key={m.id} className={'group relative flex items-center gap-1 ' + (out ? 'justify-end' : 'justify-start')}>
                  {reactingTo === m.id && (
                    <div className={'absolute top-full z-20 mt-1 flex gap-1 rounded-full bg-white px-2 py-1 shadow-md dark:bg-[#202c33] ' + (out ? 'right-2' : 'left-2')}>
                      {['👍', '❤️', '😂', '😮', '🙏', '✅'].map((e) => (
                        <button key={e} onClick={() => void react(m, e)} className="text-lg leading-none hover:scale-125">{e}</button>
                      ))}
                    </div>
                  )}
                  {actions && out && msgActions(m, out)}
                  <div className={
                    'max-w-[78%] rounded-lg px-2.5 py-1.5 text-[14.2px] shadow-sm ' +
                    (out
                      ? 'rounded-tr-none bg-[#d9fdd3] text-[#111b21] dark:bg-[#005c4b] dark:text-[#e9edef]'
                      : 'rounded-tl-none bg-white text-[#111b21] dark:bg-[#202c33] dark:text-[#e9edef]')
                  }>
                    {!out && m.payload?.referral && <AdOrigin r={m.payload.referral} />}
                    <ContentView m={m} />
                    {out && m.payload?.delivered_via && m.payload.template && m.type !== 'template' && (
                      <span className="block text-[11px] opacity-60">{t('inbox.viaTemplate', { name: m.payload.template })}</span>
                    )}
                    <span className="float-right ml-2 mt-1 flex items-center gap-1 text-[10px] text-black/45 dark:text-white/50">
                      {timeOf(m.created_at)}
                      {out && <StatusTick status={m.status} />}
                    </span>
                    {out && m.status === 'failed' && m.error && (
                      <span className="clear-both block pt-1 text-[11px] text-red-600 dark:text-red-400" title={m.error}>
                        {t('inbox.failedReason')} {m.error.length > 160 ? m.error.slice(0, 160) + '…' : m.error}
                      </span>
                    )}
                  </div>
                  {actions && !out && msgActions(m, out)}
                </div>
              );
            })}
            {/* Bolhas otimistas: o que acabou de sair (⏱) ou falhou (⚠). */}
            {ghosts.map((g) => (
              <div key={g.localId} className="flex justify-end">
                <div className={'max-w-[78%] rounded-lg rounded-tr-none px-2.5 py-1.5 text-[14.2px] shadow-sm ' +
                  (g.failed
                    ? 'bg-red-100 text-[#111b21] dark:bg-red-950/60 dark:text-[#e9edef]'
                    : 'bg-[#d9fdd3] text-[#111b21] opacity-70 dark:bg-[#005c4b] dark:text-[#e9edef]')}>
                  <span className="whitespace-pre-wrap break-words">{g.label}</span>
                  <span className="float-right ml-2 mt-1 flex items-center gap-1 text-[10px] text-black/45 dark:text-white/50">
                    {g.failed
                      ? <><AlertCircle className="size-3.5 text-red-500" /> {t('inbox.notSent')}</>
                      : <Clock className="size-3 text-black/40 dark:text-white/40" />}
                  </span>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
        )}
      </div>

      {/* Compositor — atendimento 1:1 na conversa aberta */}
      {canSend && (
        <div className="border-t border-black/5 bg-[#f0f2f5] px-3 py-2 dark:border-white/5 dark:bg-[#202c33]">
          {official && <WindowNotice state={win} fallbackName={fallbackName} />}
          {official && (mustTemplate || templateMode) && chatJid ? (
            <>
              <TemplateSender to={chatJid} instanceId={instanceId} onSent={() => setTemplateMode(false)} />
              {!mustTemplate && (
                <button onClick={() => setTemplateMode(false)} className="mt-1 text-[11px] text-black/50 underline dark:text-white/50">
                  {t('inbox.window.backToText')}
                </button>
              )}
            </>
          ) : (<>
          {replyTo && (
            <div className="mb-2 flex items-center gap-2 rounded-md border-l-4 border-[#008069] bg-white/70 px-2.5 py-1.5 text-xs dark:bg-white/10">
              <Reply className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{replyTo.label}</span>
              <button onClick={() => setReplyTo(null)} aria-label={t('inbox.cancelReply')} className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10">
                <X className="size-3.5" />
              </button>
            </div>
          )}
          {file && (
            <div className="mb-2 flex items-center gap-2 rounded-md bg-white/70 px-2.5 py-1.5 text-xs dark:bg-white/10">
              <Paperclip className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <button onClick={() => setFile(null)} aria-label={t('inbox.removeAttachment')} className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10">
                <X className="size-3.5" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={sending}
              aria-label={t('inbox.attach')}
              title={t('inbox.attach')}
              className="rounded-full p-2 text-black/55 transition-colors hover:bg-black/5 disabled:opacity-50 dark:text-white/60 dark:hover:bg-white/10"
            >
              <Paperclip className="size-5" />
            </button>
            <textarea
              rows={1}
              value={draft}
              disabled={sending}
              onChange={(e) => onTyping(e.target.value)}
              onKeyDown={(e) => {
                // Enter envia; Shift+Enter quebra linha (o hábito do WhatsApp).
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={t('inbox.typeMessage')}
              className="max-h-32 min-h-9 flex-1 resize-y rounded-lg border-0 bg-white px-3 py-2 text-[14.5px] text-[#111b21] outline-none disabled:opacity-60 dark:bg-[#2a3942] dark:text-[#e9edef]"
            />
            <button
              onClick={() => void send()}
              disabled={sending || (!draft.trim() && !file)}
              aria-label={t('inbox.send')}
              className="rounded-full bg-[#008069] p-2 text-white transition-opacity hover:opacity-90 disabled:opacity-40 dark:bg-[#00a884]"
            >
              <Send className="size-5" />
            </button>
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] text-black/45 dark:text-white/40">
            {t('inbox.sendHint')}
            {official && (
              <button onClick={() => setTemplateMode(true)} className="underline">{t('inbox.window.useTemplate')}</button>
            )}
          </p>
          </>)}
        </div>
      )}
    </>
  );

  // Embutida (Playground): a mesma conversa, sem o painel lateral.
  if (inline) {
    return (
      <div className="flex h-[560px] flex-col overflow-hidden rounded-xl border bg-[#efeae2] dark:bg-[#0b141a]">
        {inner}
      </div>
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed right-0 top-0 z-50 flex h-full w-full flex-col overflow-hidden bg-[#efeae2] shadow-2xl outline-none md:w-1/2 dark:bg-[#0b141a] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
        >
          {inner}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
